import React, { useState, useEffect } from 'react';
import './PlayerCardModal.css';

interface PlayerCardModalProps {
  positional: any;
  playerName?: string;
  playerNumber?: number | null;
  onClose: () => void;
  onHire?: (quantity: number) => void;
  onFire?: () => void;
  onUpdatePlayer?: (name: string, number: number, customPosition?: string, customKeywords?: string) => void;
  canHire?: boolean;
  isRosterFull?: boolean;
  mode?: 'hire' | 'view';
  currentCount?: number;
  maxLimit?: number;
  isRegistered?: boolean;
  onDecrement?: () => void;
  treasury?: number;
  totalHiredCount?: number;
}

const parsePlayerCost = (costStr: string | undefined): number => {
  if (!costStr) return 0;
  const clean = costStr.toUpperCase().replace(/,/g, '').trim();
  if (clean.endsWith('K')) {
    return (parseFloat(clean.slice(0, -1)) || 0) * 1000;
  }
  const parsed = parseInt(clean, 10);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCost = (cost: string | number | null | undefined): string => {
  if (cost === null || cost === undefined) return '0';
  const num = typeof cost === 'number' ? cost : parsePlayerCost(String(cost));
  return num.toLocaleString('en-US');
};

export const PlayerCardModal: React.FC<PlayerCardModalProps> = ({ 
  positional, 
  playerName = '',
  playerNumber = null,
  onClose, 
  onHire, 
  onFire,
  onUpdatePlayer,
  canHire = false,
  isRosterFull = false,
  mode = 'hire',
  currentCount = 0,
  maxLimit = 0,
  isRegistered = false,
  onDecrement,
  treasury = 0,
  totalHiredCount = 0
}) => {
  const [editName, setEditName] = useState(playerName);
  const [editNumber, setEditNumber] = useState<string>(playerNumber ? String(playerNumber) : '');
  const [editPosition, setEditPosition] = useState(() => positional ? positional.Position.split(' (')[0] : '');
  const [editKeywords, setEditKeywords] = useState(() => {
    if (!positional) return '';
    const kwMatch = positional.Position.match(/\(([^)]+)\)/);
    return kwMatch ? kwMatch[1] : '';
  });
  
  const [draftCount, setDraftCount] = useState(() => {
    if (mode === 'hire' && positional) {
      const costVal = parsePlayerCost(positional.Cost);
      const isRosterFull = (totalHiredCount || 0) >= 16;
      const isPositionalLimitReached = (currentCount || 0) >= (maxLimit || 0);
      const canAfford = (treasury || 0) >= costVal;
      if (!isRosterFull && !isPositionalLimitReached && canAfford) {
        return 1;
      }
    }
    return 0;
  });

  useEffect(() => {
    setEditName(playerName);
    setEditNumber(playerNumber ? String(playerNumber) : '');
    if (positional) {
      setEditPosition(positional.Position.split(' (')[0]);
      const kwMatch = positional.Position.match(/\(([^)]+)\)/);
      setEditKeywords(kwMatch ? kwMatch[1] : '');
    }
  }, [playerName, playerNumber, positional]);

  if (!positional) return null;

  const cleanName = positional.Position.split(' (')[0];
  const keywordsMatch = positional.Position.match(/\(([^)]+)\)/);
  const keywords = keywordsMatch ? keywordsMatch[1] : '';

  const costVal = parsePlayerCost(positional.Cost);
  const currentRemainingTreasury = treasury - (draftCount * costVal);
  const canAffordNext = currentRemainingTreasury >= costVal;
  const isRosterFullWithDrafts = (totalHiredCount + draftCount) >= 16;
  const isPositionalLimitReachedWithDrafts = (currentCount + draftCount) >= maxLimit;

  const canIncrement = !isRosterFullWithDrafts && !isPositionalLimitReachedWithDrafts && canAffordNext;

  let disableReason = '';
  if (mode === 'hire') {
    if (totalHiredCount + draftCount >= 16) {
      disableReason = 'Roster is full (max 16 players).';
    } else if (currentCount + draftCount >= maxLimit) {
      disableReason = `Maximum number of ${cleanName}s reached.`;
    } else if (draftCount === 0 && currentRemainingTreasury < costVal) {
      disableReason = 'Not enough gold in treasury.';
    }
  }

  const isMinusDisabled = draftCount === 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        
        <div className="modal-header">
          {mode === 'view' ? (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                <input 
                  type="number" 
                  value={editNumber}
                  onChange={(e) => setEditNumber(e.target.value)}
                  placeholder="#"
                  style={{ width: '40px', padding: '6px', fontSize: '16px', fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <input 
                  type="text" 
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Player Name"
                  style={{ flex: 1, padding: '6px', fontSize: '18px', fontWeight: 'bold', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
              {positional.isUnmatched ? (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>⚠️ Unmatched player. Please complete:</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '3px' }}>Position Name</label>
                      <input 
                        type="text" 
                        value={editPosition}
                        onChange={(e) => setEditPosition(e.target.value)}
                        placeholder="e.g. Star Mercenary, Ogre"
                        style={{ width: '100%', padding: '6px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: '#fff', color: '#333' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '3px' }}>Keywords (Big Guy, Troll, etc.)</label>
                      <input 
                        type="text" 
                        value={editKeywords}
                        onChange={(e) => setEditKeywords(e.target.value)}
                        placeholder="e.g. Big Guy, Treeman"
                        style={{ width: '100%', padding: '6px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: '#fff', color: '#333' }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: '#666' }}>{cleanName} {keywords && <span className="modal-keywords">{keywords}</span>}</div>
              )}
            </div>
          ) : (
            <div className="modal-header-container">
              <div className="modal-header-title-row">
                <h2>{cleanName}</h2>
                <div className="modal-header-controls">
                  <span className="positional-count-badge">
                    {currentCount + draftCount}/{maxLimit}
                  </span>
                  <button
                    type="button"
                    className="count-adjust-btn plus-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canIncrement) {
                        setDraftCount(draftCount + 1);
                      }
                    }}
                    disabled={!canIncrement}
                    title="Draft player"
                  >
                    ＋
                  </button>
                  <button
                    type="button"
                    className="count-adjust-btn minus-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (draftCount > 0) {
                        setDraftCount(draftCount - 1);
                      }
                    }}
                    disabled={isMinusDisabled}
                    title="Undraft player"
                  >
                    －
                  </button>
                </div>
              </div>
              {keywords && <span className="modal-keywords">{keywords}</span>}
            </div>
          )}
        </div>

        <div className="modal-stats">
          <div className="modal-stat-box">
            <span className="modal-stat-label">MA</span>
            <span className="modal-stat-val">{positional.MA || '-'}</span>
          </div>
          <div className="modal-stat-box">
            <span className="modal-stat-label">ST</span>
            <span className="modal-stat-val">{positional.ST || '-'}</span>
          </div>
          <div className="modal-stat-box">
            <span className="modal-stat-label">AG</span>
            <span className="modal-stat-val">{positional.AG || '-'}</span>
          </div>
          <div className="modal-stat-box">
            <span className="modal-stat-label">PA</span>
            <span className="modal-stat-val">{positional.PA || '-'}</span>
          </div>
          <div className="modal-stat-box">
            <span className="modal-stat-label">AR</span>
            <span className="modal-stat-val">{positional.AR || '-'}</span>
          </div>
        </div>

        <div className="modal-info-section">
          <div className="modal-info-row">
            <strong>Skills:</strong> {positional.Skills?.join(', ') || positional.Skills || 'None'}
          </div>
          <div className="modal-info-row">
            <strong>Categories:</strong> Primary: {positional.Primary || '-'} | Secondary: {positional.Secondary || '-'}
          </div>
          <div className="modal-info-row">
            <strong>Cost:</strong> {formatCost(positional.Cost)} GP
          </div>
        </div>

        <div className="modal-actions">
          {mode === 'hire' ? (
            <>
              <div className="modal-draft-summary" style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '14px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#334155',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontWeight: '500' }}>Drafting:</span>
                  <strong style={{ color: '#0f172a', fontSize: '15px' }}>{draftCount} {draftCount === 1 ? 'Player' : 'Players'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontWeight: '500' }}>Purchase Cost:</span>
                  <strong style={{ color: '#2563eb', fontSize: '15px' }}>{formatCost(costVal * draftCount)} GP</strong>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  borderTop: '1px dashed #cbd5e1', 
                  paddingTop: '8px', 
                  marginTop: '4px' 
                }}>
                  <span style={{ color: '#64748b', fontWeight: '500' }}>Remaining Treasury:</span>
                  <strong style={{ 
                    color: currentRemainingTreasury >= 0 ? '#16a34a' : '#dc2626',
                    fontSize: '15px' 
                  }}>
                    {formatCost(currentRemainingTreasury)} GP
                  </strong>
                </div>
              </div>
              {disableReason && <div className="modal-warning" style={{ marginBottom: '12px' }}>{disableReason}</div>}
              <button 
                className="hire-button" 
                onClick={() => { 
                  if (onHire) {
                    onHire(draftCount);
                  }
                  onClose(); 
                }}
                disabled={draftCount === 0}
              >
                {draftCount > 1 ? "Hire Players" : "Hire Player"}
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button 
                className="fire-button" 
                onClick={() => { onFire && onFire(); onClose(); }}
                style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', padding: '12px', borderRadius: '6px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Fire Player
              </button>
              <button 
                className="save-button" 
                onClick={() => { 
                  if (onUpdatePlayer) {
                    onUpdatePlayer(
                      editName, 
                      parseInt(editNumber, 10) || 0,
                      positional.isUnmatched ? editPosition : undefined,
                      positional.isUnmatched ? editKeywords : undefined
                    );
                  }
                  onClose(); 
                }}
                style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '6px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
